import { MAX_CAPTURE_EDGE } from '../domain/model';

export type CameraProblem = 'denied' | 'missing' | 'busy' | 'unknown';

export class CameraError extends Error {
  constructor(message: string, readonly problem: CameraProblem) {
    super(message);
    this.name = 'CameraError';
  }
}

function friendlyCameraError(error: unknown): CameraError {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return new CameraError('Camera access is off. A grown-up can allow it in this device’s settings.', 'denied');
    }
    if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
      return new CameraError('We could not find a camera on this device.', 'missing');
    }
    if (error.name === 'NotReadableError' || error.name === 'AbortError') {
      return new CameraError('Another app may be using the camera. Close it and try again.', 'busy');
    }
  }
  return new CameraError('The camera did not open. Please try again.', 'unknown');
}

export function cameraIsSupported(): boolean {
  return 'mediaDevices' in navigator && Boolean(navigator.mediaDevices);
}

export async function openCamera(video: HTMLVideoElement): Promise<MediaStream> {
  if (!cameraIsSupported()) {
    throw new CameraError('This browser cannot use the camera. Try current Chrome, Safari, or Edge.', 'missing');
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
    video.srcObject = stream;
    await video.play();
    return stream;
  } catch (error) {
    throw friendlyCameraError(error);
  }
}

export function closeCamera(stream: MediaStream | undefined): void {
  stream?.getTracks().forEach((track) => track.stop());
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('The picture could not be made.')),
      'image/jpeg',
      0.86,
    );
  });
}

export async function captureFrame(video: HTMLVideoElement): Promise<{ blob: Blob; width: number; height: number }> {
  if (!video.videoWidth || !video.videoHeight) {
    throw new CameraError('The camera is still waking up. Try the picture button again.', 'busy');
  }
  const scale = Math.min(1, MAX_CAPTURE_EDGE / Math.max(video.videoWidth, video.videoHeight));
  const width = Math.max(2, Math.round(video.videoWidth * scale));
  const height = Math.max(2, Math.round(video.videoHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new CameraError('The picture could not be made. Please try again.', 'unknown');
  context.drawImage(video, 0, 0, width, height);
  return { blob: await canvasBlob(canvas), width, height };
}
